import { Link } from "react-router";

import { EmptyState } from "../lib/EmptyState.tsx";
import { BORROWED_COPY } from "./loans.constants.ts";
import { useBorrowedLoansController } from "./useBorrowedLoansController.ts";
import type { BorrowedLoanAct, BorrowedLoanRow } from "./borrowedLoans.model.ts";

import "./loans.css";

/**
 * Loans somebody has offered this account, and the four answers to them.
 *
 * The borrower's half of `ondo.loans`, which had no screen at all until OND-9 —
 * so a loan carried the lender's account of itself, opened when they said so and
 * closed when they said so.
 *
 * NO BUSINESS CONDITIONALS HERE. Which controls a row offers is
 * `borrowedLoans.model.ts`'s `actsFor`, which mirrors each definer's `where`
 * clause; this file renders the list it was handed. The only `if`s below are
 * about presentation: whether a label is in flight, and whether anything is.
 */
export function BorrowedLoans({ workBase }: { workBase: string }) {
  const { view, act, busy } = useBorrowedLoansController(workBase);

  if (view.kind === "loading") {
    return (
      <p className="borrowed__status" role="status">
        {view.message}
      </p>
    );
  }

  if (view.kind === "error") {
    return (
      <section className="borrowed">
        <h2 className="borrowed__title">{view.title}</h2>
        <p className="borrowed__refusal" role="alert">
          {view.message}
        </p>
      </section>
    );
  }

  if (view.kind === "empty") {
    return (
      <EmptyState
        className="borrowed__empty"
        lead={view.message}
        detail={view.detail}
      />
    );
  }

  return (
    <section className="borrowed">
      <p className="borrowed__summary">{view.summary}</p>
      <ul className="borrowed__rows">
        {view.rows.map((row) => (
          <LoanCard
            key={row.loanId}
            row={row}
            onAct={act}
            busyAct={busy?.loanId === row.loanId ? busy.act : null}
            anyBusy={busy !== null}
          />
        ))}
      </ul>
    </section>
  );
}

/** What each act's button says, and what it says while it is in flight. */
const LABELS: Record<BorrowedLoanAct, { idle: string; busy: string }> = {
  accept: { idle: BORROWED_COPY.accept, busy: BORROWED_COPY.accepting },
  decline: { idle: BORROWED_COPY.decline, busy: BORROWED_COPY.declining },
  received: { idle: BORROWED_COPY.received, busy: BORROWED_COPY.receiving },
  returned: { idle: BORROWED_COPY.returned, busy: BORROWED_COPY.returning },
};

/** The sentence under each act, said before it is pressed rather than after. */
const NOTES: Record<BorrowedLoanAct, string | null> = {
  accept: BORROWED_COPY.acceptNote,
  decline: BORROWED_COPY.declineNote,
  received: BORROWED_COPY.receiveNote,
  returned: BORROWED_COPY.returnNote,
};

function LoanCard({
  row,
  onAct,
  busyAct,
  anyBusy,
}: {
  row: BorrowedLoanRow;
  onAct: (loanId: string, act: BorrowedLoanAct) => void;
  busyAct: BorrowedLoanAct | null;
  anyBusy: boolean;
}) {
  return (
    <li className="borrowed__row">
      <Link className="borrowed__link" to={row.href}>
        <span className="borrowed__work">{row.title}</span>
        <span className="borrowed__state">{row.state}</span>
      </Link>

      <dl className="borrowed__facts">
        <dt>{BORROWED_COPY.facts.lender}</dt>
        <dd>{row.lender}</dd>
        <dt>{BORROWED_COPY.facts.venue}</dt>
        <dd>{row.venue}</dd>
        <dt>{BORROWED_COPY.facts.dates}</dt>
        <dd>{row.span}</dd>
        <dt>{BORROWED_COPY.facts.purpose}</dt>
        <dd>{row.purpose}</dd>
      </dl>

      {row.receipt !== null && (
        <>
          <p className="borrowed__receipt">{row.receipt}</p>
          <p className="borrowed__note">{BORROWED_COPY.onTheRecord}</p>
        </>
      )}
      {row.waiting !== null && <p className="borrowed__waiting">{row.waiting}</p>}

      {row.acts.length > 0 && (
        <div className="borrowed__actions">
          {row.acts.map((which) => (
            <div className="borrowed__action" key={which}>
              <button
                type="button"
                className={`borrowed__button borrowed__button--${which}`}
                disabled={anyBusy}
                onClick={() => onAct(row.loanId, which)}
              >
                {busyAct === which ? LABELS[which].busy : LABELS[which].idle}
              </button>
              {NOTES[which] !== null && (
                <p className="borrowed__note">{NOTES[which]}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </li>
  );
}
